import { AuthExpired, NetworkError, ParseError, SourceError } from '../../lib/errors.ts';

export class VeneraRuntimeError extends SourceError {}

export class UnsupportedVeneraApiError extends VeneraRuntimeError {
  constructor(apiName: string, opts?: { source?: string; cause?: unknown }) {
    super(`Venera API is not implemented yet: ${apiName}`, opts);
  }
}

export const VENERA_HOST_ERROR_PREFIX = '__BUNKO_VENERA_HOST_ERROR__:';

const AUTH_EXPIRED_PATTERNS = [
  /401/,
  /not logged in/i,
  /login expired/i,
  /invalid login/i,
  /invalid account data/i,
  /登录失效/,
  /未登录/,
];

const PARSE_ERROR_PATTERNS = [
  /cannot read propert(?:y|ies)/i,
  /is not iterable/i,
  /is not a function/i,
  /unexpected token/i,
];

export function normalizeVeneraError(err: unknown, sourceId: string): Error {
  if (
    err instanceof AuthExpired ||
    err instanceof NetworkError ||
    err instanceof ParseError ||
    err instanceof UnsupportedVeneraApiError
  ) {
    return err;
  }
  const message = errorMessage(err);
  const hostError = parseHostError(message, sourceId, err);
  if (hostError) return hostError;
  if (AUTH_EXPIRED_PATTERNS.some((pattern) => pattern.test(message))) {
    return new AuthExpired(message, { source: sourceId, cause: err });
  }
  if (PARSE_ERROR_PATTERNS.some((pattern) => pattern.test(message))) {
    return new ParseError(message, { source: sourceId, cause: err });
  }
  return new VeneraRuntimeError(message, { source: sourceId, cause: err });
}

export function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const maybeMessage = (err as { message?: unknown }).message;
    if (typeof maybeMessage === 'string') return maybeMessage;
  }
  return String(err);
}

function parseHostError(message: string, sourceId: string, cause: unknown): Error | undefined {
  if (!message.startsWith(VENERA_HOST_ERROR_PREFIX)) return undefined;
  const encoded = message.slice(VENERA_HOST_ERROR_PREFIX.length);
  try {
    const payload = JSON.parse(encoded) as { name?: unknown; message?: unknown; status?: unknown };
    const hostMessage = typeof payload.message === 'string' ? payload.message : message;
    const hostName = typeof payload.name === 'string' ? payload.name : '';
    const status = typeof payload.status === 'number' ? payload.status : undefined;
    if (AUTH_EXPIRED_PATTERNS.some((pattern) => pattern.test(hostMessage))) {
      return new AuthExpired(hostMessage, { source: sourceId, cause });
    }
    if (
      hostName === 'UnsupportedVeneraApiError' ||
      /^Venera API is not implemented yet: /.test(hostMessage)
    ) {
      return new UnsupportedVeneraApiError(hostMessage.replace(/^Venera API is not implemented yet: /, ''), {
        source: sourceId,
        cause,
      });
    }
    if (hostName === 'NetworkError' || status !== undefined || /(?:proxyFetch|network)/i.test(hostMessage)) {
      return new NetworkError(hostMessage, {
        source: sourceId,
        status,
        cause,
      });
    }
    if (PARSE_ERROR_PATTERNS.some((pattern) => pattern.test(hostMessage))) {
      return new ParseError(hostMessage, { source: sourceId, cause });
    }
    return new VeneraRuntimeError(hostMessage, { source: sourceId, cause });
  } catch {
    return undefined;
  }
}
